import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Pill, TierBadge, timeAgo, Avatar } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { PenLine, ClipboardCheck, Scale, MessageSquarePlus, CheckCircle2, Send } from "lucide-react";

const STAGES = [
  { id: "drafting", label: "1 · Drafting", icon: PenLine, sub: "Junior VAs" },
  { id: "fulfillment_review", label: "2 · Senior VA Review", icon: ClipboardCheck, sub: "Almira / Eryel (Reyna leads)" },
  { id: "attorney_approval", label: "3 · Attorney Approval", icon: Scale, sub: "Nicole/Cielo → escalate Stephen (Joey if OOO)" },
];

const FULFILLMENT_STATUSES = ["Good to Go", "Needs Updating", "Skipped (needs higher-level)", "Out of Office", "I have a question or I'm unsure"];
const STATUS_TONE = { "Good to Go": "green", "Needs Updating": "red", "Skipped (needs higher-level)": "yellow", "Out of Office": "pale", "I have a question or I'm unsure": "cobalt" };

export default function ReviewPipeline() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const load = async () => setItems((await api.get("/review")).data);
  useEffect(() => { load(); }, []);

  const colFor = (it) => (["approved", "released", "attorney_approval"].includes(it.stage) && it.stage !== "attorney_approval")
    ? "released" : it.stage;

  const grouped = { drafting: [], fulfillment_review: [], attorney_approval: [], released: [] };
  items.forEach((it) => {
    if (it.stage === "released" || it.stage === "approved") grouped.released.push(it);
    else grouped[it.stage]?.push(it);
  });

  return (
    <div className="p-8 md:p-10">
      <PageHeader title="Review Pipeline" subtitle="Documents and Tier 3 replies flow through drafting → fulfillment review → attorney approval. Approving a Tier 3 item releases the reply." />

      <div className="grid lg:grid-cols-3 gap-5">
        {STAGES.map((st) => (
          <div key={st.id}>
            <div className="section-band rounded-lg px-4 py-3 mb-3">
              <div className="flex items-center gap-2 font-bold"><st.icon className="w-4 h-4" /> {st.label}</div>
              <div className="text-xs text-white/60 mt-0.5">{st.sub}</div>
            </div>
            <div className="space-y-3">
              {grouped[st.id].map((it) => <ReviewCard key={it.id} item={it} user={user} onChange={load} />)}
              {grouped[st.id].length === 0 && <div className="text-sm text-[#101820]/40 bg-white rounded-xl p-4 border border-black/5">Nothing here.</div>}
            </div>
          </div>
        ))}
      </div>

      {grouped.released.length > 0 && (
        <div className="mt-8">
          <div className="section-band rounded-lg px-4 py-3 mb-3 max-w-md">
            <div className="flex items-center gap-2 font-bold"><CheckCircle2 className="w-4 h-4 text-[#FFD100]" /> Released / Approved</div>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            {grouped.released.map((it) => <ReviewCard key={it.id} item={it} user={user} onChange={load} released />)}
          </div>
        </div>
      )}
    </div>
  );
}

function ReviewCard({ item, user, onChange, released }) {
  const [comment, setComment] = useState("");
  const canReview = ["reviewer", "admin"].includes(user.role);
  const canApprove = ["attorney", "admin"].includes(user.role);

  const act = async (payload) => {
    await api.post(`/review/${item.id}/action`, payload);
    toast.success("Updated");
    onChange();
  };

  return (
    <div data-testid={`review-card-${item.id}`} className="bg-white rounded-xl p-4 shadow-sm border border-black/5 card-lift">
      <div className="flex items-start justify-between gap-2">
        <div className="font-semibold text-[#141B4D] text-sm">{item.title}</div>
        {item.tier != null && <TierBadge tier={item.tier} className="scale-90 origin-right" />}
      </div>
      <div className="text-xs text-[#101820]/50 mt-1">{item.type} · {item.contact?.name || "—"} · {timeAgo(item.created)}</div>
      <div className="text-sm text-[#101820]/75 mt-2 line-clamp-3 bg-[#F2E9DB] rounded-lg p-2.5">{item.content}</div>

      <div className="flex items-center gap-2 mt-2 flex-wrap">
        {item.fulfillment_status && <Pill tone={STATUS_TONE[item.fulfillment_status] || "pale"}>{item.fulfillment_status}</Pill>}
        {item.attorney_status && <Pill tone={item.attorney_status === "Approved" ? "green" : item.attorney_status === "Pending" ? "yellow" : "red"}>Attorney: {item.attorney_status}</Pill>}
        {item.stage === "released" && <Pill tone="green"><Send className="w-3 h-3 mr-1 inline" /> Reply released</Pill>}
      </div>

      {/* comments */}
      {item.comments?.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {item.comments.map((c, i) => (
            <div key={i} className="text-xs bg-[#BDC5DB]/20 rounded-lg p-2">
              <span className="font-bold text-[#1D57A5]">{c.author.split("@")[0]}</span>: {c.text}
            </div>
          ))}
        </div>
      )}

      {!released && (
        <div className="mt-3 pt-3 border-t border-[#BDC5DB]/40 space-y-2">
          {item.stage === "fulfillment_review" && canReview && (
            <div className="flex flex-wrap gap-1.5">
              {FULFILLMENT_STATUSES.map((s) => (
                <button key={s} data-testid={`fulfillment-${item.id}-${s.slice(0,4)}`} onClick={() => act({ fulfillment_status: s })}
                  className="text-[11px] px-2 py-1 rounded-full bg-[#F2E9DB] border border-[#BDC5DB] hover:border-[#1D57A5] text-[#141B4D]">{s}</button>
              ))}
            </div>
          )}
          {item.stage === "attorney_approval" && canApprove && (
            <div className="flex gap-2">
              <Button data-testid={`approve-${item.id}`} onClick={() => act({ attorney_status: "Approved" })} className="h-8 flex-1 bg-[#006747] text-white hover:brightness-110 text-xs">Approve & release</Button>
              <Button data-testid={`reject-${item.id}`} onClick={() => act({ attorney_status: "Needs Updating" })} variant="outline" className="h-8 border-[#B91C1C] text-[#B91C1C] hover:bg-[#B91C1C] hover:text-white text-xs">Needs updating</Button>
            </div>
          )}
          {item.stage === "attorney_approval" && !canApprove && <div className="text-[11px] text-[#101820]/40">Waiting on an attorney to approve.</div>}
          {item.stage === "fulfillment_review" && !canReview && <div className="text-[11px] text-[#101820]/40">Waiting on Fulfillment Review.</div>}

          <div className="flex gap-1.5">
            <Input data-testid={`comment-${item.id}`} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="@mention a comment…" className="h-8 text-xs bg-[#F2E9DB] border-[#BDC5DB]" />
            <Button disabled={!comment.trim()} onClick={() => { act({ comment }); setComment(""); }} className="h-8 bg-[#1D57A5] text-white hover:bg-[#141B4D] px-2"><MessageSquarePlus className="w-4 h-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
